'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
);

interface ApplyFormData {
  firstName: string;
  lastName: string;
  businessName: string;
  businessWebsite: string;
  industry: string;
  monthlyVolume: string;
  phone: string;
  email: string;
  notes: string;
}

export default function ApplyPage() {
  const [formData, setFormData] = useState<ApplyFormData>({
    firstName: '',
    lastName: '',
    businessName: '',
    businessWebsite: '',
    industry: '',
    monthlyVolume: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.businessName.trim() !== '' &&
    formData.industry !== '' &&
    formData.monthlyVolume !== '' &&
    formData.phone.trim() !== '' &&
    formData.email.trim() !== '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setSubmitting(true);
    setSubmitError(null);

    const { error } = await supabase.from('leads').insert({
      first_name: formData.firstName,
      last_name: formData.lastName,
      business_name: formData.businessName,
      business_website: formData.businessWebsite || null,
      industry: formData.industry,
      monthly_volume: formData.monthlyVolume,
      phone: formData.phone,
      email: formData.email,
      notes: formData.notes || null,
    });

    setSubmitting(false);

    if (error) {
      setSubmitError('Something went wrong. Please try again or call us directly.');
      return;
    }

    // Fire Zapier webhook after successful Supabase insert
    try {
      await fetch('https://hooks.zapier.com/hooks/catch/26851371/ujh7ec7/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          business_name: formData.businessName,
          business_website: formData.businessWebsite || '',
          industry: formData.industry,
          monthly_volume: formData.monthlyVolume,
          phone: formData.phone,
          email: formData.email,
          notes: formData.notes || '',
        }),
      });
    } catch {
      // Zapier failure is non-blocking — form submission still succeeds
    }

    // Fire Apollo API directly — independent of Zapier
    try {
      await fetch('https://api.apollo.io/v1/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': process.env.NEXT_PUBLIC_APOLLO_API_KEY || '',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          organization_name: formData.businessName,
          website_url: formData.businessWebsite || undefined,
          phone_numbers: [{ raw_number: formData.phone }],
          email: formData.email,
          present_raw_address: '',
          contact_stage_id: process.env.NEXT_PUBLIC_APOLLO_STAGE_ID || '',
        }),
      });
    } catch {
      // Apollo failure is non-blocking — form submission still succeeds
    }

    setSubmitted(true);
  };

  const socialLinks = [
    {
      href: 'https://www.linkedin.com/company/getchargeact/',
      label: 'LinkedIn',
      icon: (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
          <circle cx="4" cy="4" r="2" />
        </svg>
      ),
    },
    {
      href: 'https://x.com/Getchargeact',
      label: 'X (Twitter)',
      icon: (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      href: 'https://www.instagram.com/getchargeact/',
      label: 'Instagram',
      icon: (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="17.5" cy="6.5" r="1.5" />
        </svg>
      ),
    },
    {
      href: 'https://www.tiktok.com/@getchargeact',
      label: 'TikTok',
      icon: (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.28a8.28 8.28 0 004.76 1.5v-3.4a4.85 4.85 0 01-1-.69z" />
        </svg>
      ),
    },
    {
      href: 'https://www.facebook.com/getchargeact',
      label: 'Facebook',
      icon: (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
        </svg>
      ),
    },
  ];

  const steps = [
    { num: '1', title: 'Application Review', desc: 'A ChargeAct manager reviews your application and business details.' },
    { num: '2', title: 'Discovery Call', desc: 'We reach out to discuss your business, current setup, and potential savings.' },
    { num: '3', title: 'Custom Quote', desc: 'You receive a transparent rate proposal tailored to your business.' },
    { num: '4', title: 'Start Processing', desc: "Once approved, you're set up and ready to accept payments." },
  ];

  return (
    <>
      <style>{`
        html { scroll-behavior: smooth; }
        body {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background-color: #fff;
          color: #111827;
          -webkit-font-smoothing: antialiased;
        }
        .apply-hero-gradient {
          background: linear-gradient(135deg, #041f4d 0%, #0A3D91 55%, #2F6BFF 100%);
        }
        .apply-input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1.5px solid #E5E7EB;
          background: #F9FAFB;
          color: #111827;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
        }
        .apply-input:focus {
          border-color: #2F6BFF;
          box-shadow: 0 0 0 3px rgba(47,107,255,0.15);
          background: #fff;
        }
        .apply-input::placeholder { color: #9CA3AF; }
        .apply-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 6px;
        }
        .apply-btn-submit {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 16px;
          background: #2F6BFF;
          color: #fff;
          font-weight: 700;
          font-size: 16px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          font-family: inherit;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(47,107,255,.35);
        }
        .apply-btn-submit:hover:not(:disabled) {
          background: #1a58f0;
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(47,107,255,.45);
        }
        .apply-btn-submit:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .apply-step-circle {
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 50%;
          background: #2F6BFF;
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        select.apply-input {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 36px;
        }
      `}</style>

      {/* MAIN */}
      <main>
        {/* Hero */}
        <section className="apply-hero-gradient pt-16 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
            >
              Merchant Application
            </div>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ letterSpacing: '-0.03em', lineHeight: 1.05 }}
            >
              Let&apos;s Connect &amp;<br />
              Talk Payments
            </h1>
            <p className="text-lg md:text-xl text-white/75 max-w-2xl">
              You&apos;re one step away from experiencing better payment processing. Fill out the form below and a dedicated ChargeAct manager will reach out to discuss your business and potential savings.
            </p>
          </div>
        </section>

        {/* Form + Sidebar */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="text-center py-16">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ background: '#EBF1FF' }}
                  >
                    <svg width="32" height="32" fill="none" stroke="#2F6BFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-[#111827] mb-3">Application Submitted!</h2>
                  <p className="text-[#4B5563] text-base max-w-md mx-auto">
                    Thank you! A dedicated ChargeAct manager will review your application and reach out shortly to discuss your business and potential savings.
                  </p>
                  <Link
                    href="/"
                    className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl text-white font-bold text-sm"
                    style={{ background: '#2F6BFF' }}
                  >
                    Back to Home
                  </Link>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                  {/* First + Last Name */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="apply-label" htmlFor="firstName">First Name *</label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        className="apply-input"
                        placeholder="Jane"
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="apply-label" htmlFor="lastName">Last Name *</label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        className="apply-input"
                        placeholder="Smith"
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Business Name */}
                  <div>
                    <label className="apply-label" htmlFor="businessName">Business Name *</label>
                    <input
                      id="businessName"
                      name="businessName"
                      type="text"
                      required
                      className="apply-input"
                      placeholder="Acme Corp LLC"
                      value={formData.businessName}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Business Website */}
                  <div>
                    <label className="apply-label" htmlFor="businessWebsite">Business Website</label>
                    <input
                      id="businessWebsite"
                      name="businessWebsite"
                      type="url"
                      className="apply-input"
                      placeholder="https://yourbusiness.com"
                      value={formData.businessWebsite}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Industry + Monthly Volume */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="apply-label" htmlFor="industry">Industry *</label>
                      <select
                        id="industry"
                        name="industry"
                        required
                        className="apply-input"
                        value={formData.industry}
                        onChange={handleChange}
                      >
                        <option value="">Select your industry</option>
                        <option value="Retail">Retail</option>
                        <option value="Ecommerce">Ecommerce</option>
                        <option value="Restaurant / Food Service">Restaurant / Food Service</option>
                        <option value="Beauty & Personal Care">Beauty &amp; Personal Care</option>
                        <option value="Home Services (Plumbing, Electrical, etc.)">Home Services (Plumbing, Electrical, etc.)</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Professional Services">Professional Services</option>
                        <option value="Subscription / Recurring Billing">Subscription / Recurring Billing</option>
                        <option value="High Risk">High Risk</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="apply-label" htmlFor="monthlyVolume">Monthly Processing Volume *</label>
                      <select
                        id="monthlyVolume"
                        name="monthlyVolume"
                        required
                        className="apply-input"
                        value={formData.monthlyVolume}
                        onChange={handleChange}
                      >
                        <option value="">Select volume range</option>
                        <option value="Under $10,000/month">Under $10,000/month</option>
                        <option value="$10,000 – $50,000/month">$10,000 &ndash; $50,000/month</option>
                        <option value="$50,000 – $100,000/month">$50,000 &ndash; $100,000/month</option>
                        <option value="$100,000 – $500,000/month">$100,000 &ndash; $500,000/month</option>
                        <option value="Over $500,000/month">Over $500,000/month</option>
                      </select>
                    </div>
                  </div>

                  {/* Phone + Email */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="apply-label" htmlFor="phone">Phone *</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        className="apply-input"
                        placeholder="(555) 000-0000"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="apply-label" htmlFor="email">Email *</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="apply-input"
                        placeholder="jane@yourbusiness.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="apply-label" htmlFor="notes">Additional Notes</label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={4}
                      className="apply-input"
                      style={{ resize: 'none' }}
                      placeholder="Tell us about your business, current processor, or any specific needs..."
                      value={formData.notes}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={!isFormValid || submitting}
                    className="apply-btn-submit"
                  >
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </button>

                  {submitError && (
                    <p className="text-sm text-red-600 text-center">{submitError}</p>
                  )}

                  <p className="text-xs text-[#6B7280] text-center">
                    By submitting, you agree to our{' '}
                    <Link className="text-[#2F6BFF] hover:underline" href="/terms-of-service">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link className="text-[#2F6BFF] hover:underline" href="/privacy-policy">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* What Happens Next */}
              <div
                className="rounded-2xl p-7 border"
                style={{ background: '#F9FAFB', borderColor: '#E5E7EB' }}
              >
                <h3 className="font-bold text-[#111827] text-lg mb-5">What Happens Next</h3>
                <div className="space-y-5">
                  {steps.map((step) => (
                    <div key={step.num} className="flex gap-4">
                      <div className="apply-step-circle">{step.num}</div>
                      <div>
                        <div className="font-semibold text-[#111827] text-sm">{step.title}</div>
                        <div className="text-[#6B7280] text-sm mt-0.5">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Questions Before Applying */}
              <div className="rounded-2xl p-6" style={{ background: '#0A3D91' }}>
                <h3 className="font-bold text-white mb-3">Questions Before Applying?</h3>
                <p className="text-white/75 text-sm mb-4">
                  Our team is happy to answer any questions before you submit your application.
                </p>
                <a
                  href="tel:13237840656"
                  className="inline-flex items-center gap-2 text-white font-semibold text-sm hover:text-blue-200 transition-colors"
                >
                  Call +1 (323) 784-0656
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 7h8M7 3l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-brand-border py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/assets/images/CHARGEACT_text_logo-1775251213270.png" alt="ChargeAct Logo" className="h-8 w-auto" />
            </div>
            <div className="flex items-center gap-6 text-sm font-medium text-brand-muted">
              <a href="#solutions" className="hover:text-navy transition-colors">Solutions</a>
              <a href="#faq" className="hover:text-navy transition-colors">FAQ</a>
              <a href="tel:13237840656" className="hover:text-navy transition-colors">+1 (323) 784-0656</a>
              <Link href="/apply" className="hover:text-navy transition-colors">Contact</Link>
            </div>
            <div className="flex items-center gap-4 text-xs text-brand-muted">
              <a href="/privacy" className="hover:text-navy transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-navy transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
